import React from 'react';
import MakeField from '../../fieldsStore/MakeField';
import store from '../../../../../store';
import { dataGridActions } from '../../../../../store/dataGridSlice/dataGridSlice';
import {
  TFieldEvent,
  TFieldScriptEvents,
  TFieldServerEvents,
} from '../../../../../types';
import { getSelectedRowsForSortEvent } from '../../../../../store/dataGridSlice/rows/up';
import persistentStorage from '../../../../../utils/persistentStorage';
import { noNaN } from '../../../../../utils/util';
import ApiaDataGridComponent from './ApiaDataGridComponent';

function hasEventOfType(
  serverEvents: TFieldServerEvents | TFieldScriptEvents | undefined,
  eventName: TFieldEvent,
) {
  return !!(serverEvents as unknown[])?.find(
    (event: unknown) => (event as { evtName: string }).evtName === eventName,
  );
}

export type TGridFocusStorage = {
  currentPage: number;
  focusedRow: number;
  focusedColumn: number;
};

function storeGridFocus(gridId: string) {
  const grid = store.getState().dataGridSlice[gridId];
  if (!persistentStorage.gridFocus) persistentStorage.gridFocus = {};
  persistentStorage.gridFocus = {
    ...(persistentStorage.gridFocus as Record<string, unknown>),
    [gridId]: {
      currentPage: noNaN(grid.context.grid.field.curPage),
      focusedRow: grid.focusedRow,
      focusedColumn: grid.focusedColumn,
    } as TGridFocusStorage,
  };
}

const nullComponent = React.forwardRef(() => {
  return null;
});

const ApiaDataGrid = () => {
  const Grid = React.useMemo(
    () =>
      MakeField(nullComponent, {
        getInitialValue: () => undefined,
        preSynchronize: () => true,
        doSynchronize: () => true,
        doValidate: () => true,
        preScriptEvents({
          event,
          fieldContext: {
            componentContext: {
              field: { scriptEvents },
            },
          },
        }) {
          if (
            ['gridSortUp', 'gridSortDown'].includes(event) &&
            hasEventOfType(scriptEvents, 'gridSort')
          )
            return true;
          return hasEventOfType(scriptEvents, event) ? true : 'omit';
        },
        preServerEvents: async ({
          event,
          fieldContext: {
            componentContext: {
              fieldId: gridId,
              field: { serverEvents },
            },
          },
        }) => {
          if (hasEventOfType(serverEvents, event)) return true;
          if (
            ['gridSortUp', 'gridSortDown'].includes(event) &&
            hasEventOfType(serverEvents, 'gridSort')
          )
            return true;
          switch (event) {
            case 'gridAdd':
              await store.dispatch(dataGridActions.addRow({ gridId }));
              break;
            case 'gridSortUp':
              await store.dispatch(dataGridActions.up({ gridId }));
              break;
            case 'gridSortDown':
              await store.dispatch(dataGridActions.down({ gridId }));
              break;
            case 'gridDelete':
              await store.dispatch(dataGridActions.deleteRow({ gridId }));
              break;
            default:
              break;
          }
          return 'cancel';
        },
        doScriptEvents: ({ event, fireScriptEvents }) => {
          switch (event) {
            case 'gridSortDown':
            case 'gridSortUp':
              return fireScriptEvents('gridSort');
            default:
              return fireScriptEvents(event);
          }
        },
        doServerEvents: async ({
          event,
          fireServerEvents,
          fieldContext: {
            componentContext: { fieldId: gridId },
          },
        }) => {
          const rowId = getSelectedRowsForSortEvent(gridId);
          const state = store.getState();
          storeGridFocus(gridId);
          switch (event) {
            case 'gridAdd':
              return fireServerEvents(event, {
                gridAction: 'add',
                cantRowsToAdd: 1,
              });
            case 'gridColumnSelect': {
              const { focusedColumn } = state.dataGridSlice[gridId];
              const { firingField } =
                state.dataGridSlice[gridId].columns[focusedColumn - 1];
              return fireServerEvents(event, { firingField });
            }
            case 'gridSortUp':
              return fireServerEvents('gridSort', {
                gridAction: 'up',
                rowId,
              });
            case 'gridSortDown':
              return fireServerEvents('gridSort', {
                gridAction: 'down',
                rowId,
              });
            case 'gridDelete':
              return fireServerEvents(event, { gridAction: 'delete', rowId });
            default:
              return true;
          }
        },
        willMarkAsLoaded: true,
      }),
    [],
  );
  return (
    <>
      <ApiaDataGridComponent />
      <Grid />
    </>
  );
};

export default React.memo(ApiaDataGrid);
